import React, { ReactElement, useRef } from 'react';
import { OverlayContainer, DismissButton, useOverlayPosition, useOverlayTrigger, useOverlay } from 'react-aria';
import { OverlayTriggerProps, useOverlayTriggerState } from '@react-stately/overlays';
import { mergeProps } from '@react-aria/utils';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { selectors as modalSelectors } from '@saucelabs/sl-container-modal';

import { GenericButtonProps } from './GenericButton';

export type GenericPopoverProps = {
  children: ReactElement | ReactElement[];
  trigger: ReactElement<GenericButtonProps>;
} & OverlayTriggerProps;

const GenericPopover: React.FC<GenericPopoverProps> = ({
  children,
  trigger,
}) => {
  const targetRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const isAnyModalOpen = useSelector(modalSelectors.isAnyModalOpenSelector);

  const state = useOverlayTriggerState({});
  const closeOverlay = () => state.close();

  const { overlayProps } = useOverlay(
    {
      isDismissable: !isAnyModalOpen,
      isOpen: state.isOpen,
      onClose: closeOverlay,
      shouldCloseOnInteractOutside(element) {
        const shouldPreventOverlayClose = element.closest('[data-prevent-toolbar-overlay-close]');

        if (shouldPreventOverlayClose) {
          return true;
        }

        closeOverlay();
        return false;
      },
    },
    popoverRef,
  );

  const { overlayProps: overlayPositionProps } = useOverlayPosition({
    isOpen: state.isOpen,
    overlayRef: popoverRef,
    placement: 'right',
    targetRef,
  });

  const { triggerProps } = useOverlayTrigger(
    // react-aria rewrites that internally => @react-aria/overlays/src/useOverlayTrigger.ts
    // so don't wonder if the resulting "aria-haspopup" differs
    { type: trigger.props.hasSubmenu ? 'menu' : 'dialog' },
    state,
    popoverRef,
  );

  return (
    <>
      {React.cloneElement(trigger, {
        ref: targetRef,
        // Merge the child's original props, with the new props provided by the trigger.
        // Also overrides highlighting the trigger button as long as the overlay is open
        ...mergeProps(trigger.props, {
          ...triggerProps,
          isHighlighted: state.isOpen,
        }),
      })}

      {state.isOpen && (
        // should "underlay" ds-/sl-modals
        <OverlayContainer className="z-100">
          <div
            ref={popoverRef}
            {...overlayProps}
            {...overlayPositionProps}
            className={classNames(
              {
                hidden: !state.isOpen,
              },
              'mt-0.25',
              'mb-0.25',
              'ml-0.5',
              'max-w-xl',
              'bg-white',
              'rounded',
              'filter',
              'drop-shadow-md',
              'overflow-hidden',
            )}
            role="dialog"
          >
            <div role={trigger.props.hasSubmenu ? 'menu' : 'section'}>
              {children}
            </div>

            {/* Just consumed by screen reader, invisible otherwise */}
            <DismissButton onDismiss={closeOverlay} />
          </div>
        </OverlayContainer>
      )}
    </>
  );
};
GenericPopover.displayName = 'GenericPopover';

export default GenericPopover;
